import React, { useEffect, useRef, useState } from "react";
import "./previewwindow.css";
import { useDispatch } from "react-redux";
import { setPrevAudioEnabled, setPrevVideoEnabled } from "../store/actions";

const PreviewWindow = ({ 
  onJoin, 
  onCancel, 
  connectOnlyWithAudio, 
  setConnectOnlyWithAudio,
  meetingTiming,
  joinError // ✨ Initial error message passed from parent when meeting hasn't started yet
}) => {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [videoEnabled, setVideoEnabled] = useState(!connectOnlyWithAudio);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [permissionError, setPermissionError] = useState(null);
  const [meetingStatus, setMeetingStatus] = useState(null);

  // ✨ Internal join block state — initialized from prop but updated by interval timer
  const [activeJoinError, setActiveJoinError] = useState(joinError || null);

  const dispatch = useDispatch();

  // ✨ Interval timer — re-checks every second whether joining should now be allowed.
  // This ensures the Join button auto-enables when the 1-minute window is reached,
  // even if the user stays on the preview window without refreshing.
  useEffect(() => {
    if (!meetingTiming || !meetingTiming.date || !meetingTiming.fromTime) return;

    const checkJoinAllowed = () => {
      const now = new Date();
      const meetingStartDateTime = new Date(`${meetingTiming.date}T${meetingTiming.fromTime}:00`);
      const oneMinuteBefore = new Date(meetingStartDateTime.getTime() - 60 * 1000);

      if (now >= oneMinuteBefore) {
        // ✨ We've entered the 1-minute window — clear the join block
        console.log("✅ 1-minute window reached - enabling Join button");
        setActiveJoinError(null);
      }
    };

    // Run immediately in case we're already in the window
    checkJoinAllowed();

    // Then check every second
    const interval = setInterval(checkJoinAllowed, 1000);

    return () => clearInterval(interval);
  }, [meetingTiming]);

  // Check meeting status
  useEffect(() => {
    if (!meetingTiming || !meetingTiming.date || !meetingTiming.fromTime || !meetingTiming.toTime) {
      return;
    }

    const now = new Date();
    const startDateTime = new Date(`${meetingTiming.date}T${meetingTiming.fromTime}:00`);
    const endDateTime = new Date(`${meetingTiming.date}T${meetingTiming.toTime}:00`);

    if (now < startDateTime) {
      setMeetingStatus('early');
    } else if (now >= startDateTime && now <= endDateTime) {
      setMeetingStatus('on-time');
    } else {
      setMeetingStatus('late');
    }
  }, [meetingTiming]);

  // Initialize media stream with default devices
  useEffect(() => {
    const getMediaStream = async () => {
      try {
        setPermissionError(null);
        
        const constraints = {
          video: videoEnabled ? true : false,
          audio: audioEnabled ? true : false,
        };

        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        
        setStream(mediaStream);
        
        if (videoRef.current && videoEnabled) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (error) {
        console.error("Error accessing media devices:", error);
        setPermissionError(
          "Unable to access camera/microphone. Please check permissions."
        );
      }
    };

    if (videoEnabled || audioEnabled) {
      getMediaStream();
    }

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [videoEnabled, audioEnabled]);

  const handleToggleVideo = () => {
    const newVideoState = !videoEnabled;
    setVideoEnabled(newVideoState);
    
    // ✨ Update parent state for connectOnlyWithAudio
    setConnectOnlyWithAudio(!newVideoState);
    
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = newVideoState;
      }
    }

    console.log(`📹 Camera ${newVideoState ? 'ON' : 'OFF'}`);
    console.log(`🎤 Audio-only mode: ${!newVideoState}`);
  };

  const handleToggleAudio = () => {
    const newAudioState = !audioEnabled;
    setAudioEnabled(newAudioState);
    
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = newAudioState;
      }
    }

    console.log(`🎤 Microphone ${newAudioState ? 'ON' : 'OFF'}`);
  };

  const handleJoin = () => {
    // ✨ Guard: do not allow joining if there is an active join error
    if (activeJoinError) {
      console.warn("🚫 Join blocked:", activeJoinError);
      return;
    }

    console.log('🚀 Joining meeting with settings:');
    console.log('  - Video:', videoEnabled ? 'ON' : 'OFF');
    console.log('  - Audio:', audioEnabled ? 'ON' : 'OFF');
    console.log('  - Audio-only mode:', !videoEnabled);

    // localStorage.setItem('previewVideoEnabled', videoEnabled.toString());
    // localStorage.setItem('previewAudioEnabled', audioEnabled.toString());
    
    dispatch(setPrevVideoEnabled(videoEnabled));
    dispatch(setPrevAudioEnabled(audioEnabled));

    // Stop the preview stream before joining
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    
    onJoin();
  };

  const handleCancel = () => {
    console.log('❌ Cancelled joining meeting');
    
    // Stop the preview stream
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    onCancel();
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const today = new Date();
    const meeting = new Date(dateStr);
    
    const isToday = 
      today.getFullYear() === meeting.getFullYear() &&
      today.getMonth() === meeting.getMonth() &&
      today.getDate() === meeting.getDate();

    if (isToday) return '';
    
    return meeting.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getStatusMessage = () => {
    if (!meetingStatus) return null;

    if (meetingStatus === 'early') {
      return {
        type: 'info',
        icon: '⏰',
        message: 'You\'re joining early. The meeting will start soon.'
      };
    } else if (meetingStatus === 'on-time') {
      return null;
    } else if (meetingStatus === 'late') {
      return {
        type: 'warning',
        icon: '⚠️',
        message: 'This meeting may have already ended or is ending soon.'
      };
    }
  };

  const statusMessage = getStatusMessage();

  return (
    <div className="preview_overlay">
      <div className="preview_container">
        <h2 className="preview_title">Setup Your Camera & Microphone</h2>
        
        {/* Meeting Timing Display */}
        {meetingTiming && (meetingTiming.fromTime || meetingTiming.toTime) && (
          <div className={`preview_meeting_timing ${meetingStatus === 'early' ? 'status-early' : meetingStatus === 'late' ? 'status-late' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="preview_clock_icon">
              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7V7z"/>
            </svg>
            <span className="preview_timing_text">
              {formatDate(meetingTiming.date) && (
                <span className="preview_timing_date">{formatDate(meetingTiming.date)} · </span>
              )}
              {meetingTiming.fromTime && meetingTiming.toTime 
                ? `${formatTime(meetingTiming.fromTime)} - ${formatTime(meetingTiming.toTime)}`
                : meetingTiming.fromTime 
                  ? `From: ${formatTime(meetingTiming.fromTime)}`
                  : `To: ${formatTime(meetingTiming.toTime)}`
              }
            </span>
          </div>
        )}

        {/* Status Message — hidden when activeJoinError is shown to avoid duplicate/conflicting messages */}
        {statusMessage && !activeJoinError && (
          <div className={`preview_status_message ${statusMessage.type}`}>
            <span className="status_icon">{statusMessage.icon}</span>
            <span className="status_text">{statusMessage.message}</span>
          </div>
        )}

        {/* ✨ Join Error Banner — shown when meeting hasn't started yet, blocks joining.
            Automatically disappears when the 1-minute window is reached via the interval timer. */}
        {activeJoinError && (
          <div className="preview_join_error">
            <span className="preview_join_error_icon">🚫</span>
            <span className="preview_join_error_text">{activeJoinError}</span>
          </div>
        )}
        
        <div className="preview_video_container">
          {videoEnabled ? (
            <video
              ref={videoRef}
              autoPlay
              muted
              className="preview_video"
            />
          ) : (
            <div className="preview_video_placeholder">
              <div className="preview_avatar">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="white">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
              <p>Camera is off</p>
              {/* <p className="preview_video_hint">You'll join with audio only</p> */}
            </div>
          )}
        </div>

        {permissionError && (
          <div className="preview_error">
            {permissionError}
          </div>
        )}

        <div className="preview_controls">
          <div className="preview_toggle_buttons">
            <button
              onClick={handleToggleVideo}
              className={`preview_toggle_button ${videoEnabled ? 'active' : 'inactive'}`}
              title={videoEnabled ? 'Turn camera off' : 'Turn camera on'}
            >
              {videoEnabled ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z"/>
                </svg>
              )}
              <span>{videoEnabled ? 'Camera On' : 'Camera Off'}</span>
            </button>

            <button
              onClick={handleToggleAudio}
              className={`preview_toggle_button ${audioEnabled ? 'active' : 'inactive'}`}
              title={audioEnabled ? 'Mute microphone' : 'Unmute microphone'}
            >
              {audioEnabled ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                  <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/>
                </svg>
              )}
              <span>{audioEnabled ? 'Mic On' : 'Mic Off'}</span>
            </button>
          </div>
        </div>

        {/* Settings Summary */}
        {/* <div className="preview_settings_summary">
          {!videoEnabled && (
            <div className="preview_setting_note">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              <span>Joining with audio only</span>
            </div>
          )}
          {!audioEnabled && (
            <div className="preview_setting_note warning">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
              </svg>
              <span>Your microphone is muted</span>
            </div>
          )}
        </div> */}

        <div className="preview_buttons">
          <button onClick={handleCancel} className="preview_cancel_button">
            Cancel
          </button>
          {/* ✨ Join button is disabled and visually muted when activeJoinError is present.
              Automatically re-enables when the interval timer clears activeJoinError. */}
          <button
            onClick={handleJoin}
            className="preview_join_button"
            disabled={!!activeJoinError}
            title={activeJoinError || ''}
            style={activeJoinError ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            Join Meeting
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreviewWindow;